import type { ReactElement } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { type PermissionCode, MARKET_PROMOTION_PERMISSIONS } from '@/constants/permissions';
import {
  createActionsColumn,
  createSerialColumn,
  DrillName,
  getAreaColumn,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import { HighlightText } from '@/components/common/HighlightText';
import {
  RowActionButton,
  RowActionGroup,
  RowEditDeleteButtons,
  RowViewRender,
} from '@/components/common/RowActions';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatDisplayDate, formatTime } from '@/utils/date';
import { OPTION_DETAIL_SEPARATOR } from '@/utils/filters';
import { formatNumber } from '@/utils/number';
import { BatchStageBadge } from '@/components/common/BatchStageBadge';
import {
  BUDGET_HOLDER_LABEL,
  DONATION_BATCH_STAGE_LABEL,
} from '../constants/marketPromotion.constants';
import { getBatchNumber } from '../utils/batchNumber';
import type {
  DonationBatch,
  DonationBatchMember,
  DonationBudget,
  DonationRequisition,
  MarketProject,
} from '../types/marketPromotionP2.types';

const dash = (value: string | null): string =>
  value === null || value === '' ? EMPTY_VALUE : value;
const money = (value: number): string => formatNumber(Math.round(value));
const day = (value: string | null): string =>
  value === null ? EMPTY_VALUE : formatDisplayDate(value);

interface RowActions<TRow> {
  onEdit: (row: TRow) => void;
  onDelete?: (row: TRow) => void;
}

function actionsColumn<TRow>(
  actions: RowActions<TRow>,
  getItemName: (row: TRow) => string,
  requires: { edit: PermissionCode; delete?: PermissionCode },
): ColumnDef<TRow, unknown> {
  return createActionsColumn<TRow>((item) => (
    <RowEditDeleteButtons
      item={item}
      itemName={getItemName(item)}
      onEdit={actions.onEdit}
      onDelete={actions.onDelete}
      editRequires={requires.edit}
      deleteRequires={requires.delete}
    />
  ));
}

export function getMarketProjectColumns(
  actions: RowActions<MarketProject>,
  searchText: string | undefined,
): ColumnDef<MarketProject, unknown>[] {
  const columns: ColumnDef<MarketProject, unknown>[] = [
    createSerialColumn<MarketProject>(),
    {
      accessorKey: 'name',
      header: 'Project',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={dash(row.original.name)} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      id: 'code',
      header: 'Code',
      cell: ({ row }) => (
        <HighlightText text={dash(row.original.code)} highlightedText={searchText} />
      ),
    },
    { id: 'start', header: 'Start', cell: ({ row }) => day(row.original.startDate) },
    { id: 'deadline', header: 'Deadline', cell: ({ row }) => day(row.original.deadlineDate) },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
  ];

  columns.push(
    actionsColumn(actions, (project) => project.name ?? 'project', {
      edit: MARKET_PROMOTION_PERMISSIONS.PROJECT_EDIT,
    }),
  );
  return columns;
}

export function getDonationBudgetColumns(
  actions: RowActions<DonationBudget>,
  searchText: string | undefined,
): ColumnDef<DonationBudget, unknown>[] {
  return [
    createSerialColumn<DonationBudget>(),
    {
      id: 'member',
      header: BUDGET_HOLDER_LABEL,
      cell: ({ row }) => (
        <StackedCellText
          primary={
            <HighlightText text={dash(row.original.userName)} highlightedText={searchText} />
          }
          primaryClassName="font-medium"
          secondary={row.original.designation}
        />
      ),
    },
    {
      id: 'where',
      header: 'Area / Region',
      cell: ({ row }) => (
        <StackedCellText
          primary={
            [row.original.thanaName, row.original.districtName].filter(Boolean).join(', ') ||
            EMPTY_VALUE
          }
          secondary={row.original.department}
        />
      ),
    },
    {
      id: 'item',
      header: 'Donation Item',
      cell: ({ row }) => (
        <WrappingCellText>
          <HighlightText text={dash(row.original.itemName)} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      id: 'budget',
      header: 'Budget',
      cell: ({ row }) => <span className="tabular-nums">{money(row.original.budgetAmount)}</span>,
    },
    {
      id: 'disburse',
      header: 'Disbursed',
      cell: ({ row }) => <span className="tabular-nums">{money(row.original.disburseAmount)}</span>,
    },
    { id: 'budgetDate', header: 'Budget Date', cell: ({ row }) => day(row.original.budgetDate) },
    { id: 'targetDate', header: 'Target Date', cell: ({ row }) => day(row.original.targetDate) },
    actionsColumn(actions, (budget) => `budget of ${budget.userName ?? 'this holder'}`, {
      edit: MARKET_PROMOTION_PERMISSIONS.BUDGET_EDIT,
    }),
  ];
}

export const DONATION_BATCH_COLUMN_ID = 'batch';

export function getDonationBatchColumns(
  getMembersLink: (batch: DonationBatch) => ReactElement,
  action: {
    label: string;
    onClick: (batch: DonationBatch) => void;
    when: (batch: DonationBatch) => boolean;
  },
  searchText: string | undefined,
): ColumnDef<DonationBatch, unknown>[] {
  return [
    createSerialColumn<DonationBatch>(),
    {
      id: DONATION_BATCH_COLUMN_ID,
      header: 'Batch',
      cell: ({ row }) => {
        const batchNumber = String(getBatchNumber(row.original));
        return (
          <DrillName
            value={`#${batchNumber}`}
            label={`Members of batch ${batchNumber}`}
            render={getMembersLink(row.original)}
            highlightedValue={
              <HighlightText text={`#${batchNumber}`} highlightedText={searchText} />
            }
          />
        );
      },
    },
    {
      id: 'name',
      header: 'Batch Name',
      cell: ({ row }) => (
        <WrappingCellText>
          <HighlightText text={dash(row.original.name)} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      id: 'item',
      header: 'Donation Item',
      cell: ({ row }) => (
        <WrappingCellText>
          <HighlightText text={dash(row.original.itemName)} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      id: 'covering',
      header: 'Covering Work',
      cell: ({ row }) => `${day(row.original.trnDateFrom)} – ${day(row.original.trnDateTo)}`,
    },
    {
      id: 'published',
      header: 'Published',
      cell: ({ row }) => (
        <StackedCellText
          primary={day(row.original.publishDate)}
          secondary={formatTime(row.original.publishDate)}
        />
      ),
    },
    {
      id: 'publishedBy',
      header: 'Published By',
      cell: ({ row }) => dash(row.original.publishedBy),
    },
    {
      id: 'amount',
      header: 'Amount',
      cell: ({ row }) => <span className="tabular-nums">{money(row.original.promoItemQty)}</span>,
    },
    {
      id: 'stage',
      header: 'Batch Status',
      cell: ({ row }) => (
        <BatchStageBadge stage={row.original.stage} stageLabels={DONATION_BATCH_STAGE_LABEL} />
      ),
    },
    createActionsColumn<DonationBatch>((batch) => {
      const batchNumber = String(getBatchNumber(batch));
      return (
        <RowActionGroup>
          {action.when(batch) ? (
            <RowActionButton
              label={action.label}
              ariaLabel={`${action.label} batch ${batchNumber}`}
              onClick={() => action.onClick(batch)}
            />
          ) : null}
          <RowViewRender
            render={getMembersLink(batch)}
            ariaLabel={`Details of batch ${batchNumber}`}
          />
        </RowActionGroup>
      );
    }),
  ];
}

export const DONATION_MEMBER_COLUMN_ID = 'member';

export function getDonationMemberColumns(): ColumnDef<DonationBatchMember, unknown>[] {
  return [
    createSerialColumn<DonationBatchMember>(),
    {
      id: DONATION_MEMBER_COLUMN_ID,
      header: 'ZM Name',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.fullName)}
          primaryClassName="font-medium"
          secondary={[row.original.hrEmpId, row.original.designationName]
            .filter(Boolean)
            .join(OPTION_DETAIL_SEPARATOR)}
        />
      ),
    },
    { id: 'reporting', header: 'Reporting', cell: ({ row }) => dash(row.original.reportingName) },
    { id: 'account', header: 'Account No', cell: ({ row }) => dash(row.original.accountNumber) },
    { id: 'mobile', header: 'Mobile', cell: ({ row }) => dash(row.original.mobile) },
    getAreaColumn<DonationBatchMember>(),
    {
      id: 'donations',
      header: 'Donations',
      cell: ({ row }) => <span className="tabular-nums">{row.original.donationCount}</span>,
    },
    {
      id: 'amount',
      header: 'Total',
      cell: ({ row }) => <span className="tabular-nums">{money(row.original.donationAmount)}</span>,
    },
  ];
}

export function getRequisitionColumns(
  actions?: RowActions<DonationRequisition>,
): ColumnDef<DonationRequisition, unknown>[] {
  const columns: ColumnDef<DonationRequisition, unknown>[] = [
    createSerialColumn<DonationRequisition>(),
    {
      id: 'institute',
      header: 'Institute',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.instituteName)}
          primaryClassName="font-medium"
          secondary={[row.original.instituteCode, row.original.instituteEiin]
            .filter(Boolean)
            .join(OPTION_DETAIL_SEPARATOR)}
        />
      ),
    },
    {
      id: 'teacher',
      header: 'Teacher',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.teacherName)}
          secondary={row.original.teacherPhone}
        />
      ),
    },
    { id: 'specimen', header: 'Specimen', cell: ({ row }) => dash(row.original.specimenName) },
    { id: 'bookType', header: 'Book Type', cell: ({ row }) => dash(row.original.bookTypeName) },
    {
      id: 'students',
      header: 'Students',
      cell: ({ row }) => <span className="tabular-nums">{row.original.studentQty ?? '—'}</span>,
    },
    {
      id: 'target',
      header: 'Target Sale',
      cell: ({ row }) => <span className="tabular-nums">{money(row.original.targetSaleQty)}</span>,
    },
    {
      id: 'budget',
      header: 'Budget',
      cell: ({ row }) => <span className="tabular-nums">{money(row.original.budgetAmount)}</span>,
    },
    {
      id: 'signoff',
      header: 'Sign-off',
      cell: ({ row }) => {
        const stages = [
          row.original.isChecked ? 'Checked' : null,
          row.original.isApproved ? 'Approved' : null,
          row.original.isAudit ? 'Audited' : null,
        ].filter(Boolean);
        return stages.length === 0 ? (
          <span className="text-xs text-muted-foreground">Pending</span>
        ) : (
          <span className="text-xs">{stages.join(' · ')}</span>
        );
      },
    },
    {
      id: 'entered',
      header: 'Entered',
      cell: ({ row }) => (
        <StackedCellText primary={day(row.original.enteredAt)} secondary={row.original.enteredBy} />
      ),
    },
  ];

  if (actions) {
    columns.push(
      actionsColumn(actions, (requisition) => requisition.specimenName ?? 'requisition', {
        edit: MARKET_PROMOTION_PERMISSIONS.REQUISITION_EDIT,
      }),
    );
  }
  return columns;
}
